import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const TestIntelligence = ({ project }) => {
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatedTests, setGeneratedTests] = useState(null);

  useEffect(() => {
    fetchTestCases();
  }, [project.id]);

  const fetchTestCases = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/test-cases`);
      setTestCases(response.data);
    } catch (error) {
      console.error('Error fetching test cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAITests = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/ai/generate-tests`, {
        project_id: project.id,
        analysis_type: 'test_generation',
        context: {
          project_name: project.name,
          migration_type: project.migration_type,
          modules: ['FI', 'SD', 'MM'],
          custom_code_changes: 50,
          simplification_items: 15,
          high_risk_areas: ['Universal Journal', 'Business Partner', 'Material Ledger']
        }
      });
      setGeneratedTests(response.data.result.generated_tests);
    } catch (error) {
      console.error('Error generating tests:', error);
      alert('Error during test generation');
    } finally {
      setGenerating(false);
    }
  };

  const statusColors = {
    draft: 'bg-gray-100 text-gray-800',
    ready: 'bg-blue-100 text-blue-800',
    executed: 'bg-yellow-100 text-yellow-800',
    passed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800'
  };

  const priorityColors = {
    low: 'border-l-gray-300',
    medium: 'border-l-yellow-400',
    high: 'border-l-orange-400',
    critical: 'border-l-red-500'
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>;
  }

  const testsByModule = testCases.reduce((acc, test) => {
    if (!acc[test.module]) acc[test.module] = [];
    acc[test.module].push(test);
    return acc;
  }, {});

  const stats = {
    total: testCases.length,
    passed: testCases.filter((t) => t.status === 'passed').length,
    failed: testCases.filter((t) => t.status === 'failed').length,
    pending: testCases.filter((t) => ['draft', 'ready'].includes(t.status)).length,
    ai_generated: testCases.filter((t) => t.created_by_ai).length
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Test Intelligence</h2>
            <p className="text-gray-600 mt-1">AI-powered test case generation and tracking</p>
          </div>
          <button
            onClick={generateAITests}
            disabled={generating}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50"
          >
            {generating ? 'Generating...' : '🤖 Generate AI Tests'}
          </button>
        </div>

        {generatedTests && (
          <div className="mb-6 p-6 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">🤖</span>
              AI-Generated Test Cases
            </h3>
            <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">
              {generatedTests}
            </pre>
            <button
              onClick={() => setGeneratedTests(null)}
              className="mt-4 px-4 py-2 bg-white hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium text-gray-700"
            >
              Clear Generated Tests
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
            <div className="text-blue-600 text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-gray-700 font-medium mt-1">Total Tests</div>
          </div>
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
            <div className="text-green-600 text-2xl font-bold">{stats.passed}</div>
            <div className="text-sm text-gray-700 font-medium mt-1">Passed</div>
          </div>
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
            <div className="text-red-600 text-2xl font-bold">{stats.failed}</div>
            <div className="text-sm text-gray-700 font-medium mt-1">Failed</div>
          </div>
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
            <div className="text-yellow-600 text-2xl font-bold">{stats.pending}</div>
            <div className="text-sm text-gray-700 font-medium mt-1">Pending</div>
          </div>
          <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
            <div className="text-purple-600 text-2xl font-bold">{stats.ai_generated}</div>
            <div className="text-sm text-gray-700 font-medium mt-1">AI Generated</div>
          </div>
        </div>

        {Object.entries(testsByModule).map(([module, tests]) => (
          <div key={module} className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <span className="w-3 h-3 rounded-full bg-blue-600 mr-2"></span>
              {module} Module ({tests.length} tests)
            </h3>
            <div className="space-y-2">
              {tests.map((test) => (
                <div
                  key={test.id}
                  className={`border-l-4 ${priorityColors[test.priority]} bg-white border border-gray-200 rounded p-3 hover:shadow-md transition-shadow`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="font-mono text-sm font-medium text-gray-700">{test.test_id}</span>
                      <span className="text-gray-900 font-medium">{test.title}</span>
                      {test.created_by_ai && (
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                          AI
                        </span>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[test.status]}`}>
                      {test.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{test.description}</p>
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex space-x-4">
                      <span className="text-gray-600">
                        <span className="font-medium">Type:</span> {test.test_type}
                      </span>
                      <span className="text-gray-600">
                        <span className="font-medium">Duration:</span> {test.execution_time_minutes}min
                      </span>
                      <span className="text-gray-600">
                        <span className="font-medium">Priority:</span> {test.priority}
                      </span>
                    </div>
                    {test.defects_found > 0 && (
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-medium">
                        {test.defects_found} defects
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
